import { V3Routes } from './../../../../routes/index';
import { action, computed, observable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../../commonv3/api/RemoteResourceV3';
import { EventReservationIndexView } from '../../../../types/eventReservations';
import { EventReservationId } from '../../../../../commonv3/types/eventReservations';
import { EventReservationApis } from '../../../../apis/eventReservations';
import { DialogState } from '../../../../../commonv3/states/dialogState';
import { translateErrorMessage } from '../../../../../commonv3/api/protocol';
import { ReservationIndexView } from '../../../../types/reservations';
import { ReservationApis, ReservationSummaryApis } from '../../../../apis/reservations';
import { ReservableType } from '../../../../../commonv3/types/reservable';
import { ReservationId } from '../../../../../commonv3/types/reservations';
import { SessionType } from '../../../../types/sessions';
import { GlobalStatesV3 } from '../../../../../commonv3/states/GlobalStatesV3';
import { SubmitState } from '../../../../../commonv3/states/SubmitState';
import { ReservationSummary } from '../../../../types/reservationSummaries';
import { AllReservationIndexView } from '../../../molecules/reservations/ReservationCardList';
import { CancelFormState } from './CancelFormState';
import { Service } from '../../../../../commonv3/types/service';

export enum CancelStep {
    CONFIRMATION = 'confirmation',
    FORM = 'form',
}

export class ReservationListState {
    @observable eventReservations: RemoteResourceV3<EventReservationIndexView[]> = RemoteResourceV3.loading();
    @observable reservations: RemoteResourceV3<ReservationIndexView[]> = RemoteResourceV3.loading();
    @observable reservationSummary: RemoteResourceV3<ReservationSummary> = RemoteResourceV3.loading();
    @observable cancelFormState: CancelFormState = new CancelFormState();
    @observable cancelDialog: DialogState = new DialogState(false);
    @observable cancelStep: CancelStep = null;
    @observable cancellingReservableType: ReservableType;
    @observable cancellingReservationId: EventReservationId | ReservationId = null;
    @observable processingState: SubmitState = new SubmitState();

    async init() {
        await updateRemoteResourceV3(
            this.eventReservations,
            (v) => (this.eventReservations = v),
            () => EventReservationApis.findAll(Service.LIKES)
        );
        await updateRemoteResourceV3(
            this.reservations,
            (v) => (this.reservations = v),
            () => ReservationApis.findAll()
        );
        await updateRemoteResourceV3(
            this.reservationSummary,
            (v) => (this.reservationSummary = v),
            () => ReservationSummaryApis.show()
        );
        this.cancelFormState.init();
    }

    isReserved(reservableType: ReservableType, reservableId: number): boolean {
        return !!this.allReservationList.find(
            (r) => r.reservable_type === reservableType && r.reservable.id === reservableId
        );
    }

    findSessionReservationsByType(type: SessionType): ReservationIndexView[] {
        return this.reservations.getOrElse([]).filter((r) => r.reservable.session_type === type);
    }

    @action.bound
    requestCancel(reservation: EventReservationIndexView | ReservationIndexView) {
        this.cancelStep = CancelStep.CONFIRMATION;
        this.cancellingReservableType = reservation.reservable_type;
        this.cancellingReservationId = reservation.id;
        this.cancelDialog.open();
    }

    @computed
    get allReservationList(): (AllReservationIndexView)[] {
        return [...this.eventReservations.getOrElse([]), ...this.reservations.getOrElse([])];
    }

    @computed
    get loading(): boolean {
        return this.reservations.loading || this.eventReservations.loading || this.reservationSummary.loading;
    }

    @computed
    get submitting(): boolean {
        return this.processingState.submitting;
    }

    @computed
    get isInitialCoachingVisible() {
        if (this.reservationSummary.isEmpty()) {
            return false;
        }
        return this.reservationSummary.get().visible_any_of_the_initial_coachings;
    }

    @computed
    get reservableCoachingTypes() {
        if (this.reservationSummary.isEmpty()) {
            return [];
        }
        return this.reservationSummary.get().reservable_coaching_keys;
    }

    @computed
    get visibleCoachingTypes() {
        if (this.reservationSummary.isEmpty()) {
            return [];
        }
        return this.reservationSummary.get().visible_coaching_keys;
    }

    @action.bound
    async confirmCancel() {
        if (this.cancellingReservableType === ReservableType.TRIAL_LESSON) {
            this.cancelStep = CancelStep.FORM;
        } else {
            await this.submitCancel();
        }
    }

    @action.bound
    async submitCancel() {
        await this.processingState.operate(async () => {
            try {
                switch (this.cancellingReservableType) {
                    case ReservableType.EVENT:
                    case ReservableType.TRIAL_LESSON:
                        await EventReservationApis.cancel(
                            this.cancellingReservationId,
                            this.cancelFormState.getDirtyValue()
                        );
                        break;
                    case ReservableType.SESSION:
                        await ReservationApis.cancel(this.cancellingReservationId);
                        break;
                    default:
                        throw new Error('Unsupported reservable type: ' + this.cancellingReservableType);
                }
                await Promise.all([this.init(), GlobalStatesV3.currentUser.reloadUser()]);
                GlobalStatesV3.routing.push(V3Routes.reservationCancelComplete());
            } catch (e) {
                alert('キャンセルに失敗しました：' + translateErrorMessage(e));
            } finally {
                this.cancelDialog.close();
            }
        });
    }

    @computed
    get cancellingReservation() {
        return this.allReservationList.find(
            (r) => r.id == this.cancellingReservationId && r.reservable_type == this.cancellingReservableType
        );
    }

    @action.bound
    async requestRescheduleTrialReservation() {
        GlobalStatesV3.routing.push(V3Routes.trialLessonReschedule(this.cancellingReservationId));
    }
}
